import React, { useMemo, useRef, useState } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartLine,
  faArrowTrendUp,
  faArrowTrendDown,
} from '@fortawesome/free-solid-svg-icons';
import AppChart, {
  ChartSeries,
  ChartSlice,
} from 'shared/components/Chart/AppChart';
import ReportDataTable, {
  ReportColumn,
} from 'shared/components/Table/ReportDataTable';
import DrillBreadcrumb from 'shared/components/Dashboard/DrillBreadcrumb';
import {
  drillHint,
  drillLevelLabel,
  drillSearchPlaceholder,
  scopeLabel,
} from 'shared/json/drilldown';
import {
  useGetSchoolTrendsQuery,
  useGetSchoolTrendsDrillQuery,
  useGetAllDistrictListQuery,
  useGetAllBlockListQuery,
  useGetStudentChildCountAllYearQuery,
  useGetCommonMastersQuery,
} from './Dashboard.service';

import './TrendAnalysis.scss';

/** Coerce a value (may be a string via Convstr) to a number. */
const toNum = (v: unknown): number => {
  if (typeof v === 'number') return Number.isNaN(v) ? 0 : v;
  if (typeof v === 'string') {
    const n = Number(v.replace(/,/g, '').trim());
    return Number.isNaN(n) ? 0 : n;
  }
  return 0;
};

/** Line colors cycled through for the per-district trend lines. */
const DISTRICT_COLORS = [
  '#f4c430',
  '#ef4444',
  '#8bc34a',
  '#8e1f5b',
  '#26a69a',
  '#3b82f6',
  '#7e57c2',
  '#f0803c',
  '#f59e0b',
  '#22c55e',
  '#06b6d4',
  '#d9584f',
];

/** Parse the START year from a name like "2026-27" → 2026. acd_yr ids are not
 * chronological, so the name's start year is the only reliable sort key. */
const startYearOf = (name: string): number =>
  Number(String(name).match(/(\d{4})/)?.[1] ?? NaN);

/**
 * Trend Analysis view. State-level charts (Enrolment, Boys vs Girls) + the
 * year-wise table come from schooltrends (full year range, no slicing). The
 * top-districts multi-line chart + the districts-change table come from
 * schooltrendsdrill, where the FROM-year selector drives the per-district
 * change (selected year → latest year). API data only.
 */
/** Classification filters shared from the Dashboard header (id strings). */
interface TrendFilters {
  management: string[];
  category: string[];
  schoolType: string[];
}

export interface TrendAnalysisProps {
  /** Selected geography ids from the header (null = state-wide). */
  districtId?: number | null;
  blockId?: number | null;
  schoolId?: number | null;
  /** Selected geography NAMES from the header (for the "currently viewing"
   * context bar — mirrors the Numbers tab). */
  district?: string | null;
  block?: string | null;
  school?: string | null;
  /** Selected academic year id — needed to resolve school names by block. */
  acdYr?: number | null;
  /** Management / Category / School-Type filter selections. */
  filters?: TrendFilters;
  /** Header selection setters — clicking a row syncs the header dropdowns so
   * the selected district/block shows there, and the "Back" bar clears a level
   * (same handlers the other tabs use). Values are entity NAMES. */
  onDistrictChange?: (value: string) => void;
  onBlockChange?: (value: string) => void;
  onSchoolChange?: (value: string) => void;
}

const EMPTY_FILTERS: TrendFilters = {
  management: [],
  category: [],
  schoolType: [],
};

const TrendAnalysis: React.FC<TrendAnalysisProps> = ({
  districtId = null,
  blockId = null,
  schoolId = null,
  district = null,
  block = null,
  school = null,
  acdYr = null,
  filters = EMPTY_FILTERS,
  onDistrictChange,
  onBlockChange,
  onSchoolChange,
}) => {
  const [performers, setPerformers] = useState<'top' | 'bottom'>('top');
  const [showCount, setShowCount] = useState(12);

  // Report-table pagination preservation across drill-down. `reportFirst` is
  // the controlled page offset; `pageByLevelRef` remembers the offset of each
  // level (district / block) so going Back restores the page the user drilled
  // from instead of resetting to page 1.
  const [reportFirst, setReportFirst] = useState(0);
  const pageByLevelRef = useRef<Record<string, number>>({});

  // Local drill override, set by clicking a table row (district → its blocks,
  // block → its schools). Falls back to the header selection from props. A
  // click captures the row's id + name so we can scope the drill and show a
  // breadcrumb back up. Reset whenever the header selection changes.
  const [localDistrict, setLocalDistrict] = useState<{
    id: number;
    name: string;
  } | null>(null);
  const [localBlock, setLocalBlock] = useState<{
    id: number;
    name: string;
  } | null>(null);

  // Header selection changes supersede any local drill — reset the override.
  // Done during render (not in an effect) by comparing the previous header
  // signature, so there's no cascading-render setState-in-effect.
  const headerSig = `${districtId ?? ''}|${blockId ?? ''}|${schoolId ?? ''}`;
  const [prevHeaderSig, setPrevHeaderSig] = useState(headerSig);
  if (prevHeaderSig !== headerSig) {
    setPrevHeaderSig(headerSig);
    setLocalDistrict(null);
    setLocalBlock(null);
  }

  // Effective ids: a local drill override wins over the header props.
  const effDistrictId = localDistrict?.id ?? districtId ?? null;
  const effBlockId = localBlock?.id ?? blockId ?? null;

  // Build the schooltrendsdrill request body from the effective district/block/
  // school + classification selection. Only sets keys that are present, and
  // converts filter id strings → the number arrays the endpoint expects, e.g.
  // { district_id: 1, block_id: 4, school_id: 4768, manage_id: [1],
  //   cate_id: [1,3], school_type_id: [1,2] }. Memoized so the query only
  // refetches when the selection changes.
  const drillBody = useMemo(() => {
    const toIds = (ids: string[]) =>
      ids.map(Number).filter((n) => !Number.isNaN(n));
    const manage = toIds(filters.management);
    const cate = toIds(filters.category);
    const stype = toIds(filters.schoolType);
    // Intentionally does NOT send school_id. The drill endpoint returns the
    // block's full school list (correct id space), which is what lets
    // resolvedSchoolId map a header-selected school name → its real school_id.
    // Passing the header's schoollist id here (a DIFFERENT id space) filtered
    // the drill rows down to nothing, so resolvedSchoolId stayed null and the
    // trend charts fell back to block-level numbers for a selected school.
    return {
      ...(effDistrictId != null && { district_id: effDistrictId }),
      ...(effBlockId != null && { block_id: effBlockId }),
      ...(manage.length > 0 && { manage_id: manage }),
      ...(cate.length > 0 && { cate_id: cate }),
      ...(stype.length > 0 && { school_type_id: stype }),
    };
  }, [effDistrictId, effBlockId, filters]);

  // The drill entity level implied by the selection: a selected block → the
  // rows are SCHOOLS; a selected district (no block) → BLOCKS; otherwise →
  // DISTRICTS. Drives the row labels, titles and search placeholders below.
  const drillLevel: 'district' | 'block' | 'school' =
    effBlockId != null
      ? 'school'
      : effDistrictId != null
        ? 'block'
        : 'district';
  const levelLabel =
    drillLevel === 'school'
      ? 'Schools'
      : drillLevel === 'block'
        ? 'Blocks'
        : 'Districts';

  // "Currently viewing" context from the header selection — the deepest
  // selected name wins (school → block → district), mirroring the Numbers tab.
  // Null when nothing is selected (state-wide view).
  const viewingName =
    (school && school.trim()) ||
    (block && block.trim()) ||
    (district && district.trim()) ||
    '';
  // The level above the viewed entity, for a "Back to … — <level>" style label.
  const viewingBackLabel = school
    ? `Back to ${block ?? ''} — Schools`
    : block
      ? `Back to ${district ?? ''} — Blocks`
      : district
        ? 'Back to Tamil Nadu — Districts'
        : '';

  // Back bar → clear the deepest selected header level, stepping up one
  // (school → block → district → state) via the shared header setters.
  const handleViewingBack = () => {
    if (school) {
      onSchoolChange?.('');
    } else if (block) {
      onBlockChange?.('');
      // Back to the block list → restore its saved page.
      setReportFirst(pageByLevelRef.current.block ?? 0);
    } else if (district) {
      onDistrictChange?.('');
      // Back to the district list → restore its saved page.
      setReportFirst(pageByLevelRef.current.district ?? 0);
    }
  };

  // Row click → select the clicked entity. Districts row → drill to its blocks;
  // blocks row → drill to its schools; schools row → SELECT that school (shows
  // the "Currently viewing" bar). Syncs the header dropdown by name so the
  // selection shows there; keeps a local override as a fallback.
  const handleRowDrill = (row: Record<string, unknown>) => {
    const id = Number(row.id ?? 0);
    const name = String(row.name ?? '');
    if (drillLevel === 'school') {
      // School level is the leaf → SELECT that school so this tab's own
      // school view (its enrolment / boys-girls trend charts, narrowed to the
      // one school) shows. This does NOT open the Numbers-style detail card.
      if (name) onSchoolChange?.(name);
      return;
    }
    if (!id) return;
    // Remember the page we drilled from (for the current level) and reset the
    // next level's table to page 1.
    pageByLevelRef.current[drillLevel] = reportFirst;
    setReportFirst(0);
    if (drillLevel === 'district') {
      if (onDistrictChange) onDistrictChange(name);
      else setLocalDistrict({ id, name });
    } else if (drillLevel === 'block') {
      if (onBlockChange) onBlockChange(name);
      else setLocalBlock({ id, name });
    }
    // School level is handled above (opens the detail view).
  };

  // Breadcrumb back up one level (schools → blocks → districts).
  const goBack = () => {
    if (localBlock) {
      setLocalBlock(null);
      // Returning to the block list → restore its saved page.
      setReportFirst(pageByLevelRef.current.block ?? 0);
    } else if (localDistrict) {
      setLocalDistrict(null);
      // Returning to the district list → restore its saved page.
      setReportFirst(pageByLevelRef.current.district ?? 0);
    }
  };

  // ── Drill breadcrumb wiring ──
  // Effective names for the trail: a local row-click override wins over the
  // header selection (same precedence as effDistrictId / effBlockId above).
  const effDistrictName = localDistrict?.name ?? district ?? null;
  const effBlockName = localBlock?.name ?? block ?? null;

  // Current geography scope for the page heading, using the same effective
  // names so the title tracks a row-click drill as well as the header.
  const trendScopeName = scopeLabel({
    district: effDistrictName,
    block: effBlockName,
    school,
  });

  // Back unwinds the local override first (it sits below the header
  // selection); once there is none left, step the header selection up.
  const handleCrumbBack = () => {
    if (localBlock || localDistrict) goBack();
    else handleViewingBack();
  };

  // Crumb clicks clear a level. Each one also drops any local override at or
  // below that level, so the trail and the table never disagree.
  const crumbDistrictChange = (value: string) => {
    setLocalDistrict(null);
    setLocalBlock(null);
    setReportFirst(pageByLevelRef.current.district ?? 0);
    onDistrictChange?.(value);
  };
  const crumbBlockChange = (value: string) => {
    setLocalBlock(null);
    setReportFirst(pageByLevelRef.current.block ?? 0);
    onBlockChange?.(value);
  };

  // ── API data ──
  // Drill rows (schooltrendsdrill) power the top-N multi-line chart + the
  // per-entity change table.
  const { data: drill } = useGetSchoolTrendsDrillQuery(drillBody);

  // Schools of the drilled district (studentchildcountallyear). Its `school_id`
  // is the id space schooltrends expects — NOT the header/schoollist id space.
  // Declared here (above resolvedSchoolId) because the resolver reads it to map
  // a selected school name → its real school_id. Scoped by the drilled district
  // + academic year; skipped until a block is active (i.e. viewing schools).
  const { data: schoolChildCount } = useGetStudentChildCountAllYearQuery(
    { acd_yr: acdYr as number, district_id: effDistrictId as number },
    {
      skip: acdYr == null || effDistrictId == null || drillLevel !== 'school',
    }
  );

  // Resolve the selected school's CORRECT id from studentchildcountallyear
  // (schoolChildCount) by name. The header `schoolId` comes from schoollist (a
  // DIFFERENT id space), which made schooltrends return block-level numbers for
  // a selected school. Names are normalized AND stripped of a trailing
  // " (udise)" suffix so a header value like "APS … (33331101811)" still
  // matches the bare API `school_name`.
  const resolvedSchoolId = useMemo<number | null>(() => {
    if (!school) return null;
    const norm = (s: string) =>
      String(s)
        .replace(/\s*\(\d[\d/]*\)\s*$/, '') // drop a trailing "(udise)"
        .trim()
        .toLowerCase();
    const target = norm(school);
    const row = (schoolChildCount?.result ?? []).find(
      (sc) => norm(String(sc.school_name ?? '')) === target
    );
    const id = row ? Number(row.school_id ?? 0) : 0;
    return id > 0 ? id : null;
  }, [school, schoolChildCount]);

  // Body for the trend charts (Enrolment, Boys vs Girls, year-wise table).
  // schooltrends accepts the same geography + classification scope, so the
  // charts follow the selected district / block / school. For a selected
  // school we use the schoolChildCount-resolved id (correct id space).
  const trendsBody = useMemo(() => {
    const csv = (ids: string[]) =>
      ids
        .map(Number)
        .filter((n) => !Number.isNaN(n))
        .join(',');
    const manage = csv(filters.management);
    const cate = csv(filters.category);
    const stype = csv(filters.schoolType);
    // Only scope the trend to a school using the drill-resolved id (correct id
    // space). The header's schoollist id is a DIFFERENT space, so sending it
    // returned block-level numbers for a selected school — don't fall back to
    // it. If a school is selected but not yet resolved, we stay at block scope
    // for a tick until the drill rows arrive and resolvedSchoolId fills in.
    const effSchoolId = resolvedSchoolId ?? null;
    return {
      ...(effDistrictId != null && { district_id: effDistrictId }),
      ...(effBlockId != null && { block_id: effBlockId }),
      ...(effSchoolId != null && { school_id: effSchoolId }),
      ...(manage && { manage_id: manage }),
      ...(cate && { cate_id: cate }),
      ...(stype && { school_type_id: stype }),
    };
  }, [effDistrictId, effBlockId, resolvedSchoolId, filters]);

  const { data: trends } = useGetSchoolTrendsQuery(trendsBody);
  const { data: allDistricts } = useGetAllDistrictListQuery();
  const { data: commonMasters } = useGetCommonMastersQuery();

  // acd_yr id → { name, sort } from commonmasters.academic_year.
  const yearInfoById = useMemo(() => {
    const map = new Map<string, { name: string; sort: number }>();
    (commonMasters?.result?.academic_year ?? []).forEach((y) => {
      if (y?.id == null) return;
      const name = String(y.name ?? '');
      map.set(String(y.id), { name, sort: startYearOf(name) });
    });
    return map;
  }, [commonMasters]);

  // district_id → district name (from alldistrictlist).
  const districtIdToName = useMemo(() => {
    const map = new Map<string, string>();
    (allDistricts?.result?.schooldist ?? []).forEach((d) => {
      if (d?.id != null && d.district_name)
        map.set(String(d.id), d.district_name);
    });
    return map;
  }, [allDistricts]);

  // Blocks of the drilled district — fetched so the block rows can show their
  // real names (the drill rows only carry ids). Skipped until a district is
  // active (i.e. while viewing districts).
  const { data: blockList } = useGetAllBlockListQuery(
    { district_id: effDistrictId as number },
    { skip: effDistrictId == null }
  );

  // block_id → block name (from allblocklist). The array key varies, so we
  // check the common shapes.
  const blockIdToName = useMemo(() => {
    const map = new Map<string, string>();
    const r = blockList?.result;
    const rows = r?.schoolblock ?? r?.block ?? r?.blocks ?? [];
    rows.forEach((b) => {
      if (b?.id != null && b.block_name) map.set(String(b.id), b.block_name);
    });
    return map;
  }, [blockList]);

  // Schools of the drilled district — fetched so the school rows can show
  // their real names. We use studentchildcountallyear (NOT schoollist) because
  // its `school_id` is the SAME id space as the drill rows, whereas the
  // schoollist `id` is a different space and wouldn't match. (Declared earlier,
  // above resolvedSchoolId, since that resolver reads it.)

  // school_id → school name (from studentchildcountallyear).
  const schoolIdToName = useMemo(() => {
    const map = new Map<string, string>();
    (schoolChildCount?.result ?? []).forEach((sc) => {
      if (sc?.school_id != null && sc.school_name)
        map.set(String(sc.school_id), String(sc.school_name));
    });
    return map;
  }, [schoolChildCount]);

  // school_id → udise_code (from studentchildcountallyear), shown beside the
  // school name in the grid.
  const schoolIdToUdise = useMemo(() => {
    const map = new Map<string, string>();
    (schoolChildCount?.result ?? []).forEach((sc) => {
      if (sc?.school_id != null && sc.udise_code != null)
        map.set(String(sc.school_id), String(sc.udise_code));
    });
    return map;
  }, [schoolChildCount]);

  // ── Year-wise trend (schooltrends) — FULL range, no slicing. Scoped by the
  // effective district/block/school, so these are state-level only while
  // nothing is selected. ──
  const stateRows = useMemo(() => {
    const rows = trends?.result ?? [];
    return rows
      .map((r) => {
        const info = yearInfoById.get(String(r.acd_yr));
        const year = info?.name || String(r.acd_yr);
        const boys = toNum(r.total_boys);
        const girls = toNum(r.total_girls);
        // Students = the API's own total_students column (NOT boys + girls),
        // so the Enrolment Trend line and the year-wise table show exactly what
        // the backend reports. Fall back to boys + girls only if the API omits
        // the field (e.g. an older response shape).
        const apiStudents = toNum(r.total_students);
        return {
          year,
          sort: (info?.sort ?? Number(r.acd_yr)) || 0,
          schools: toNum(r.total_schools),
          boys,
          girls,
          //students: apiStudents > 0 ? apiStudents : boys + girls,
          students: apiStudents,
          teachers: toNum(r.total_teachers),
        };
      })
      .sort((a, b) => a.sort - b.sort);
  }, [trends, yearInfoById]);

  const trendYears = useMemo(() => stateRows.map((r) => r.year), [stateRows]);

  // Enrolment trend (line) — total students per year (full range).
  const enrolmentSeries: ChartSeries[] = useMemo(
    () => [
      {
        label: 'Students',
        data: stateRows.map((r) => r.students),
        color: '#2f5fb0',
      },
    ],
    [stateRows]
  );

  // Boys vs Girls (grouped bars) — full range.
  const boysGirlsSeries: ChartSeries[] = useMemo(
    () => [
      { label: 'Boys', data: stateRows.map((r) => r.boys), color: '#26a69a' },
      { label: 'Girls', data: stateRows.map((r) => r.girls), color: '#f0803c' },
    ],
    [stateRows]
  );

  // Memoized year-wise trend charts. They depend ONLY on their own data (not
  // on the Compare-From year, Top/Bottom toggle or SHOW count), so changing
  // those controls does NOT re-render / re-animate these charts.
  //
  // Titles use trendScopeName, not the header-only `viewingName`: schooltrends
  // is queried with the EFFECTIVE ids (which include a row-click drill), so
  // after drilling from the grid these charts hold that district's figures and
  // the heading has to say so rather than "Tamil Nadu".
  const enrolmentTrendChart = useMemo(
    () => (
      <AppChart
        type="line"
        title={`Enrolment Trend — ${trendScopeName}`}
        icon="fa-solid fa-arrow-trend-down"
        labels={trendYears}
        tools={[
          { icon: 'pi pi-refresh', label: 'Refresh' },
          { icon: 'pi pi-file', label: 'Table View' },
          { icon: 'pi pi-chart-bar', label: 'Bar view' },
          { icon: 'pi pi-chart-line', label: 'Line view' },
          { icon: 'pi pi-download', label: 'Export' },
        ]}
        series={enrolmentSeries}
        yTitle="Students"
        height={280}
      />
    ),
    [trendYears, enrolmentSeries, trendScopeName]
  );

  const boysGirlsTrendChart = useMemo(
    () => (
      <AppChart
        type="bar"
        title={`Boys vs Girls Trend — ${trendScopeName}`}
        icon="fa-solid fa-venus-mars"
        labels={trendYears}
        series={boysGirlsSeries}
        yTitle="Students"
        height={280}
        tools={[
          { icon: 'pi pi-refresh', label: 'Refresh' },
          { icon: 'pi pi-file', label: 'Table View' },
          { icon: 'pi pi-chart-bar', label: 'Bar view' },
          { icon: 'pi pi-chart-line', label: 'Line view' },
          { icon: 'pi pi-download', label: 'Export' },
        ]}
      />
    ),
    [trendYears, boysGirlsSeries, trendScopeName]
  );

  // ── District-level drill (schooltrendsdrill). ──

  // Year ids that ACTUALLY carry data in the drill rows, chronologically
  // ordered. The masters list spans decades; the drill rows only hold the
  // populated years. We intersect so the axis/series cover only real years.
  const dataYearIds = useMemo(() => {
    const rows = drill?.result ?? [];
    const ordered = Array.from(yearInfoById.entries())
      .sort((a, b) => a[1].sort - b[1].sort)
      .map(([id]) => id);
    if (rows.length === 0) return ordered;
    const present = new Set<string>();
    rows.forEach((r) => {
      ordered.forEach((yid) => {
        if (toNum(r[yid]) !== 0) present.add(yid);
      });
    });
    const filtered = ordered.filter((yid) => present.has(yid));
    return filtered.length > 0 ? filtered : ordered;
  }, [drill, yearInfoById]);

  // Chronological year-name axis for the district lines (matches dataYearIds).
  const districtYears = useMemo(
    () => dataYearIds.map((id) => yearInfoById.get(id)?.name || id),
    [dataYearIds, yearInfoById]
  );

  const yearOptions = useMemo(
    () => districtYears.slice(0, -1).map((y) => ({ label: y, value: y })),
    [districtYears]
  );
  const [fromYearSel, setFromYearSel] = useState<string | null>(null);
  const fromYear =
    fromYearSel && districtYears.includes(fromYearSel)
      ? fromYearSel
      : (districtYears[0] ?? '');
  const toYear = districtYears[districtYears.length - 1] ?? '';

  // Slice the chronological year ids to the selected FROM→TO window
  // (inclusive). This is the span the top-districts chart draws, so changing
  // the FROM year zooms the chart to that range (e.g. 2023-24 → 2026-27).
  const rangeYearIds = useMemo(() => {
    const fromIdx = dataYearIds.findIndex(
      (id) => yearInfoById.get(id)?.name === fromYear
    );
    const toIdx = dataYearIds.findIndex(
      (id) => yearInfoById.get(id)?.name === toYear
    );
    const lo = fromIdx === -1 ? 0 : Math.min(fromIdx, toIdx);
    const hi = toIdx === -1 ? dataYearIds.length - 1 : Math.max(fromIdx, toIdx);
    return dataYearIds.slice(lo, hi + 1);
  }, [dataYearIds, yearInfoById, fromYear, toYear]);

  // Year-name axis for the chart, sliced to the selected range.
  const rangeYears = useMemo(
    () => rangeYearIds.map((id) => yearInfoById.get(id)?.name || id),
    [rangeYearIds, yearInfoById]
  );

  // Build per-district change rows from the flat drill rows. CHANGE is between
  // the selected FROM year and the latest (TO) year; `series` is the trend
  // SLICED to the selected FROM→TO range for the multi-line chart.
  const districtChanges = useMemo(() => {
    const rows = drill?.result ?? [];
    const fromId = rangeYearIds[0];
    const toId = rangeYearIds[rangeYearIds.length - 1];
    return (
      rows
        .map((r, i) => {
          // Entity id/name shift by drill level: districts → blocks → schools.
          const id = String(r.district_id ?? r.block_id ?? r.school_id ?? '');
          // Resolve a REAL name only: embedded in the drill row, else from the
          // district / block / school master lists. No numeric "Block <id>"
          // fallback — unresolved rows are dropped below.
          const name =
            String(r.school_name ?? '') ||
            String(r.block_name ?? '') ||
            String(r.district_name ?? '') ||
            (drillLevel === 'school' ? schoolIdToName.get(id) : undefined) ||
            (drillLevel === 'block' ? blockIdToName.get(id) : undefined) ||
            (drillLevel === 'district'
              ? districtIdToName.get(id)
              : undefined) ||
            '';
          // At school level, show the UDISE code in brackets beside the name.
          const udise =
            drillLevel === 'school'
              ? String(r.udise_code ?? schoolIdToUdise.get(id) ?? '').trim()
              : '';
          const displayName = udise ? `${name} (${udise})` : name;
          // Series covers only the selected range (sliced).
          const series = rangeYearIds.map((yid) => toNum(r[yid]));
          const start = fromId ? toNum(r[fromId]) : (series[0] ?? 0);
          const end = toId ? toNum(r[toId]) : (series[series.length - 1] ?? 0);
          const difference = end - start;
          const changePct = start > 0 ? ((end - start) / start) * 100 : 0;
          return {
            id: Number(id) || 0, // entity id, used for row-click drill-down
            name,
            displayName,
            start,
            end,
            difference,
            changePct: Math.round(changePct * 10) / 10,
            series,
            color: DISTRICT_COLORS[i % DISTRICT_COLORS.length],
          };
        })
        // Drop entities whose name couldn't be resolved (would otherwise show
        // as "Block 473" / "District 23") — named blocks/schools only.
        .filter((d) => d.name.trim() !== '')
    );
  }, [
    drill,
    rangeYearIds,
    districtIdToName,
    blockIdToName,
    schoolIdToName,
    schoolIdToUdise,
    drillLevel,
  ]);

  // Ranked by Change % (desc) so the table and chart share one ordering.
  const changeRows = useMemo(
    () => [...districtChanges].sort((a, b) => b.changePct - a.changePct),
    [districtChanges]
  );

  // Top/bottom N districts by Change % → multi-line series.
  const rankedDistricts = useMemo(() => {
    const sorted = [...changeRows].sort((a, b) =>
      performers === 'top'
        ? b.changePct - a.changePct
        : a.changePct - b.changePct
    );
    return sorted.slice(0, showCount);
  }, [changeRows, performers, showCount]);

  // When a specific school is selected, show ONLY that school's line (matched
  // by name) instead of the ranked top/bottom N. Otherwise show the ranked set.
  const chartEntities = useMemo(() => {
    if (school) {
      const norm = (sName: string) => sName.trim().toLowerCase();
      const match = changeRows.find((d) => norm(d.name) === norm(school));
      return match ? [match] : [];
    }
    return rankedDistricts;
  }, [school, changeRows, rankedDistricts]);

  const topDistrictSeries: ChartSeries[] = chartEntities.map((d) => ({
    label: d.name,
    data: d.series,
    color: d.color,
    asLine: true,
  }));

  const allEntriesTooltip = useMemo(() => {
    const map: Record<string, { label: string; value: string }[]> = {};
    rangeYears.forEach((yearLabel, yi) => {
      const rows = changeRows
        .map((d) => ({ name: d.name, value: d.series[yi] ?? 0 }))
        .sort((a, b) => b.value - a.value)
        .map((d) => ({
          label: d.name,
          value: d.value.toLocaleString('en-IN'),
        }));
      map[String(yearLabel)] = rows;
    });
    return map;
  }, [rangeYears, changeRows]);

  // Selected school's own entity (matched by name) — derived from changeRows
  // only, NOT from the top/bottom ranked set, so toggling Top/Bottom performers
  // does NOT change it (and the memoized school charts below don't re-animate).
  const selectedSchoolEntity = useMemo(() => {
    if (!school) return null;
    const norm = (s: string) => s.trim().toLowerCase();
    return changeRows.find((d) => norm(d.name) === norm(school)) ?? null;
  }, [school, changeRows]);

  // Memoized school-level trend charts. They depend only on the selected
  // school's data (not performers / showCount), so clicking Top/Bottom does NOT
  // reload / re-animate them.
  const schoolEnrolmentChart = useMemo(() => {
    if (!selectedSchoolEntity) return null;
    return (
      <AppChart
        key={`school-enrol-${school}`}
        type="line"
        title={`Enrolment Trend — ${school}`}
        icon="fa-solid fa-arrow-trend-down"
        labels={rangeYears}
        series={[
          {
            label: 'Students',
            data: selectedSchoolEntity.series,
            color: '#2f5fb0',
            asLine: true,
          },
        ]}
        yTitle="Students"
        height={280}
      />
    );
  }, [selectedSchoolEntity, school, rangeYears]);

  const schoolBoysGirlsChart = useMemo(() => {
    // Use the REAL per-year boys/girls from schooltrends (now scoped to the
    // selected school via resolvedSchoolId → school_id). `boysGirlsSeries` and
    // `trendYears` are built from the same school-scoped `stateRows` the state
    // chart uses, so the bars show the API's actual total_boys / total_girls
    // per year instead of splitting a yearly total by one fixed current-year
    // ratio. Only render once the school-scoped rows have arrived.
    if (!school || stateRows.length === 0) return null;
    return (
      <AppChart
        key={`school-bg-${school}`}
        type="bar"
        title={`Boys vs Girls Trend — ${school}`}
        icon="fa-solid fa-venus-mars"
        labels={trendYears}
        series={boysGirlsSeries}
        yTitle="Students"
        height={280}
      />
    );
  }, [school, stateRows, trendYears, boysGirlsSeries]);

  // When a specific SCHOOL is selected, show ITS OWN enrolment trend (single
  // Districts change table columns. District / block names are links (drill
  // chevron); at school level the name is plain text since school-level
  // click-to-view-details is disabled.
  const districtColumns: ReportColumn[] = [
    {
      // 'link' at every level: district/block rows drill deeper, a school row
      // (the leaf) opens its School Detail view.
      key: 'displayName',
      label: drillLevelLabel(drillLevel),
      type: 'link',
    },
    { key: 'start', label: fromYear, type: 'number' },
    { key: 'end', label: toYear, type: 'number' },
    { key: 'difference', label: 'Difference', type: 'delta', hideArrow: true },

    { key: 'changePct', label: 'Change %', type: 'percent' },
  ];

  // Year-wise state table columns.
  const yearColumns: ReportColumn[] = [
    { key: 'year', label: 'Year', type: 'text', sortable: false },
    { key: 'schools', label: 'Schools', type: 'number', sortable: false },
    { key: 'boys', label: 'Boys', type: 'number', sortable: false },
    { key: 'girls', label: 'Girls', type: 'number', sortable: false },
    {
      key: 'students',
      label: 'Students',
      type: 'number',
      bold: true,
      sortable: false,
    },
    { key: 'teachers', label: 'Teachers', type: 'number', sortable: false },
  ];

  // Unused import guard for ChartSlice (kept for future pie/doughnut trends).
  void (null as unknown as ChartSlice[]);

  return (
    <div className="trend">
      {/* ── Header row ── */}
      <div className="trend__head dash-section m-2">
        <div className="trend__title-wrap">
          <FontAwesomeIcon icon={faChartLine} className="trend__icon" />
          <h5 className="trend__title">
            Trend Analysis — {trendScopeName}
            <span className="trend__subtitle ms-2">
              {' '}
              {fromYear ?? ''} to {trendYears[trendYears.length - 1] ?? ''}
            </span>
          </h5>
        </div>
      </div>

      {/* ── Currently-viewing context bar (mirrors the Numbers tab) ── */}
      {/* {viewingName && (
        <div className="trend__viewing-bar dash-section m-2">
          <button
            type="button"
            className="trend__viewing-back"
            onClick={handleViewingBack}
          >
            <i className="pi pi-arrow-left" /> {viewingBackLabel}
          </button>
          <span className="trend__viewing">
            Currently viewing: <strong>{viewingName}</strong>
          </span>
          <span className="trend__viewing-home">
            <i className="pi pi-home" /> Tamil Nadu
          </span>
        </div>
      )} */}

      <div className="trend__controls dash-section m-2">
        <div className="trend__compare">
          <span className="trend__label">COMPARE FROM</span>
          <Dropdown
            value={fromYear}
            options={yearOptions}
            onChange={(e) => setFromYearSel(e.value)}
            className="trend__year-dd"
            panelClassName="trend__year-dd-panel"
          />
          <span className="trend__to">to</span>
          <input
            type="text"
            className="trend__year-dd trend__year-box"
            value={toYear}
            readOnly
            aria-label="To year (latest)"
          />
        </div>

        <div className="trend__controls-right">
          <div className="trend__toggle">
            <button
              type="button"
              className={performers === 'top' ? 'is-active' : ''}
              onClick={() => setPerformers('top')}
            >
              <FontAwesomeIcon icon={faArrowTrendUp} /> Top performers
            </button>
            <button
              type="button"
              className={performers === 'bottom' ? 'is-active' : ''}
              onClick={() => setPerformers('bottom')}
            >
              <FontAwesomeIcon icon={faArrowTrendDown} /> Bottom performers
            </button>
          </div>
          <span className="trend__show">
            <span className="trend__label">SHOW</span>
            <Dropdown
              value={showCount}
              options={[5, 10, 12, 15, 20, 38].map((n) => ({
                label: `${n}`,
                value: n,
              }))}
              onChange={(e) => setShowCount(e.value)}
              className="trend__year-dd"
              panelClassName="trend__year-dd-panel"
            />
          </span>
        </div>
      </div>
      {/* ── Enrolment + Boys/Girls charts (scoped to the current selection) ──

          For a SELECTED SCHOOL the schooltrends table returns block-level
          numbers (id-space mismatch), so the Enrolment Trend is driven from the
          drill-matched school series (correct per-school values). */}
      <div className="row g-3 dash-section">
        <div className="col-12 col-lg-6">
          {schoolEnrolmentChart ?? enrolmentTrendChart}
        </div>
        <div className="col-12 col-lg-6">
          {schoolBoysGirlsChart ?? boysGirlsTrendChart}
        </div>
      </div>

      {/* ── Trend chart: only the selected school when one is chosen, else the
          Top/Bottom N multi-line chart. ── */}
      <div className="dash-section">
        <AppChart
          // Remount when the selection / toggle / count changes so the lines
          // refresh (same-length series otherwise reuse the chart instance).
          key={`${school || `${performers}-${showCount}`}-${levelLabel}`}
          type="line"
          title={
            school
              ? `Top 1 School — ${school}`
              : `${
                  performers === 'top' ? 'Top' : 'Bottom'
                } ${showCount} ${levelLabel} by Change`
          }
          subtitle={
            school
              ? 'selected school'
              : 'click a line or legend entry to drill down'
          }
          icon="fa-solid fa-ranking-star"
          labels={rangeYears}
          series={topDistrictSeries}
          // Hover lists the districts for that year in DESCENDING value order
          // (highest first) instead of raw series order. (#53)
          tooltipSort="desc"
          yTitle="Students"
          height={320}
          tools={[
            { icon: 'pi pi-refresh', label: 'Refresh' },
            { icon: 'pi pi-file', label: 'Table View' },
            { icon: 'pi pi-chart-bar', label: 'Bar view' },
            { icon: 'pi pi-chart-line', label: 'Line view' },
            { icon: 'pi pi-download', label: 'Export' },
          ]}
        />
      </div>

      {/* ── Districts/Blocks/Schools change table ── */}
      <div className="dash-section">
        <DrillBreadcrumb
          district={effDistrictName}
          block={effBlockName}
          school={school}
          onBack={handleCrumbBack}
          onDistrictChange={crumbDistrictChange}
          onBlockChange={crumbBlockChange}
          onSchoolChange={onSchoolChange}
        />
        <ReportDataTable
          title={`${levelLabel} — Change from ${fromYear} to ${toYear}`}
          hint={drillHint(drillLevel)}
          icon="fa-solid fa-table-list"
          // Count shown in the header; the level is governed by the top
          // dropdown so the per-grid paginator is turned off. (#54)
          countBadge={`${chartEntities.length} ${levelLabel.toLowerCase()}`}
          paginated={false}
          data={chartEntities as unknown as Record<string, unknown>[]}
          // columns={districtColumns}
          columns={
            drillLevel === 'school' && chartEntities.length === 1
              ? [
                  { ...districtColumns[0], type: 'text' },
                  ...districtColumns.slice(1),
                ]
              : districtColumns
          }
          exportFileName={`${levelLabel.toLowerCase()}-change`}
          searchPlaceholder={drillSearchPlaceholder(drillLevel)}
          rows={12}
          onRowClick={handleRowDrill}
          first={reportFirst}
          onPage={setReportFirst}
        />
      </div>

      {/* ── Year-wise state table (full range) ── */}
      <div className="dash-section">
        <ReportDataTable
          title={`Year-wise Enrolment — ${trendScopeName}`}
          icon="fa-solid fa-table-list"
          data={stateRows as unknown as Record<string, unknown>[]}
          columns={yearColumns}
          exportFileName="year-wise-state"
          // Search box enabled so a year can be looked up directly. (#56)
          searchPlaceholder="Search year..."
          rows={12}
        />
      </div>
    </div>
  );
};

export default TrendAnalysis;
